import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Compass } from "lucide-react";
import { lazy, type ComponentType, type ReactNode } from "react";
import { createBrowserRouter, Link, Navigate, RouterProvider, useLocation } from "react-router";
import { Layout, RequireRole } from "@/components/Layout";
import { ConfirmProvider, Empty, Loading, ToastProvider } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/auth";
import type { Role } from "@/lib/roles";
import { Dashboard } from "@/pages/Dashboard";
import { Login } from "@/pages/Login";


/*
 * After a new release the old file names are gone, so a page opened before it can't load the
 * next screen ("Failed to fetch dynamically imported module"). Reload once to get the new
 * files; if that was tried in the last few seconds, show the error instead of looping.
 */
const RELOADED_AT = "newsroom:reloaded-for-update";

function page<M>(load: () => Promise<M>, pick: (module: M) => ComponentType) {
  return lazy(() =>
    load().then(
      (module) => ({ default: pick(module) }),
      (error) => {
        let last: number;
        try {
          last = Number(sessionStorage.getItem(RELOADED_AT)) || 0;
          sessionStorage.setItem(RELOADED_AT, String(Date.now()));
        } catch {
          throw error; // without storage we can't tell a fresh release from a reload loop
        }
        if (Date.now() - last < 10_000) throw error;
        window.location.reload();
        return new Promise<never>(() => {}); // keep showing the loader until the page reloads
      },
    ),
  );
}

/* Pages other than the dashboard load on first visit, keeping the first screen small. */
const ContentList = page(() => import("@/pages/content/ContentList"), (m) => m.ContentList);
const ItemEditor = page(() => import("@/pages/content/ItemEditor"), (m) => m.ItemEditor);
const Imports = page(() => import("@/pages/Imports"), (m) => m.Imports);
const IssueEditor = page(() => import("@/pages/newsletter/IssueEditor"), (m) => m.IssueEditor);
const IssuesList = page(() => import("@/pages/newsletter/IssuesList"), (m) => m.IssuesList);
const Subscribers = page(() => import("@/pages/newsletter/Subscribers"), (m) => m.Subscribers);
const MagazineEditor = page(() => import("@/pages/magazine/MagazineEditor"), (m) => m.MagazineEditor);
const MagazineList = page(() => import("@/pages/magazine/MagazineList"), (m) => m.MagazineList);
const Settings = page(() => import("@/pages/Settings"), (m) => m.Settings);
const Sources = page(() => import("@/pages/Sources"), (m) => m.Sources);
const Users = page(() => import("@/pages/Users"), (m) => m.Users);
const Visitors = page(() => import("@/pages/Visitors"), (m) => m.Visitors);
const VisitorDetail = page(() => import("@/pages/Visitors"), (m) => m.VisitorDetail);
const Activity = page(() => import("@/pages/Operations"), (m) => m.Activity);
const Agents = page(() => import("@/pages/Operations"), (m) => m.Agents);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      // A 4xx will not get better by asking again.
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
    },
  },
});

/** Signed-in only; remembers where you were going. */
function Protected({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <Loading label="Restoring your session…" />;
  if (status === "signed-out") return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

const gate = (minimum: Role, page: ReactNode) => <RequireRole minimum={minimum}>{page}</RequireRole>;

function NotFound() {
  return (
    <div className="card">
      <Empty title="This page doesn’t exist" icon={<Compass size={24} />}>
        <Link className="btn btn--primary" to="/" style={{ marginTop: 8 }}>
          Back to the dashboard
        </Link>
      </Empty>
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  {
    path: "/",
    element: (
      <Protected>
        <Layout />
      </Protected>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "content", element: <ContentList /> },
      { path: "content/:id", element: <ItemEditor /> },
      { path: "newsletter", element: gate("editor", <IssuesList />) },
      { path: "newsletter/subscribers", element: gate("admin", <Subscribers />) },
      { path: "newsletter/new", element: gate("editor", <IssueEditor />) },
      { path: "newsletter/:id", element: gate("editor", <IssueEditor />) },
      { path: "magazine", element: <MagazineList /> },
      { path: "magazine/new", element: gate("editor", <MagazineEditor />) },
      { path: "magazine/:id", element: <MagazineEditor /> },
      { path: "sources", element: gate("admin", <Sources />) },
      { path: "agents", element: gate("admin", <Agents />) },
      { path: "activity", element: gate("admin", <Activity />) },
      { path: "users", element: gate("super_admin", <Users />) },
      { path: "visitors", element: gate("super_admin", <Visitors />) },
      { path: "visitors/:id", element: gate("super_admin", <VisitorDetail />) },
      { path: "settings", element: gate("super_admin", <Settings />) },
      { path: "imports", element: gate("super_admin", <Imports />) },
      // Addresses of the public site (blogs.avioxtechnologies.com) typed or auto-completed here, often
      // as the page to return to after signing in: send them to the matching list instead of "not found".
      { path: "news/*", element: <Navigate to="/content?content_type=section:news" replace /> },
      { path: "blogs/*", element: <Navigate to="/content?content_type=section:blogs" replace /> },
      { path: "story/*", element: <Navigate to="/content" replace /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
